import { useEffect } from 'react';

interface LightboxProps {
  image: { src: string; alt: string };
  currentIndex: number;
  totalImages: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export default function Lightbox({ 
  image, 
  currentIndex, 
  totalImages, 
  onClose, 
  onPrev, 
  onNext 
}: LightboxProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/50 hover:bg-black/70 rounded-full transition-colors z-10 border border-white/10 md:border-none md:bg-white/10 md:hover:bg-white/20"
      >
        <i className="ri-close-line text-2xl md:text-3xl text-white"></i>
      </button>

      {/* Image Counter */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 text-white text-base md:text-lg font-semibold z-10 drop-shadow-md">
        {currentIndex + 1} / {totalImages}
      </div>

      {/* Previous Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        className="absolute left-2 md:left-6 w-10 h-10 md:w-14 md:h-14 flex items-center justify-center bg-black/30 hover:bg-black/50 rounded-full transition-colors z-10 border border-white/10 md:border-none md:bg-white/10 md:hover:bg-white/20"
      >
        <i className="ri-arrow-left-s-line text-2xl md:text-3xl text-white"></i>
      </button>

      {/* Next Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className="absolute right-2 md:right-6 w-10 h-10 md:w-14 md:h-14 flex items-center justify-center bg-black/30 hover:bg-black/50 rounded-full transition-colors z-10 border border-white/10 md:border-none md:bg-white/10 md:hover:bg-white/20"
      >
        <i className="ri-arrow-right-s-line text-2xl md:text-3xl text-white"></i>
      </button>

      {/* Image */}
      <div 
        className="relative flex w-full h-[100dvh] items-center justify-center p-0 md:p-8 lg:p-16 max-w-7xl mx-auto pointer-events-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative max-h-full max-w-full flex pointer-events-auto">
          <img
            src={image.src}
            alt={image.alt}
            className="w-auto h-auto max-w-full max-h-full object-contain md:rounded-xl shadow-2xl"
          />
          {/* Watermark Logo */}
          <div className="absolute top-4 right-4 md:top-6 md:right-8 opacity-90 pointer-events-none select-none">
            <img 
              src="https://static.readdy.ai/image/462a574349d19c517babfc55bf63e079/c8ac21bc970c8b7e4f4c5b80839b9d5d.png" 
              alt="KARE YAPI INSAAT" 
              className="h-8 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md" 
            />
          </div>
        </div>
      </div>
    </div>
  );
}